<template>
  <div ref="root" class="hs-custom-select" :class="{ 'is-open': isOpen, 'is-disabled': disabled }">
    <button ref="trigger" type="button" aria-haspopup="listbox" :aria-expanded="isOpen" class="hs-custom-select__button" :disabled="disabled" @click="toggleOpen"
      @keydown.down.prevent="openMenu" @keydown.enter.prevent="toggleOpen" @keydown.space.prevent="toggleOpen">
      <vue-feather v-if="icon" :type="icon" size="16" />
      <span class="hs-custom-select__label" :class="{ 'is-placeholder': !selectedOption }">
        {{ selectedOption ? selectedOption.label : placeholder }}
      </span>
      <vue-feather type="chevron-down" size="16" class="hs-custom-select__chevron" />
    </button>

    <teleport to="body">
      <transition name="hs-custom-select-pop">
        <div v-if="isOpen" ref="menu" class="hs-custom-select__menu" :style="menuStyle" @keydown="handleMenuKeydown">
          <div v-if="searchable" class="hs-custom-select__search">
            <input ref="search" v-model="searchQuery" type="search" :placeholder="searchPlaceholder"
              :aria-label="searchPlaceholder" class="hs-custom-select__search-input" autocomplete="off" />
          </div>
          <div class="hs-custom-select__options" role="listbox" :aria-label="placeholder">
          <button v-for="option in filteredOptions" :key="String(option.value)" type="button"
            class="hs-custom-select__option" :class="{ 'is-selected': isSelected(option.value) }" role="option"
            :aria-selected="isSelected(option.value)" @click="selectOption(option)">
            <span>{{ option.label }}</span>
            <vue-feather v-if="isSelected(option.value)" type="check" size="15" />
          </button>
          <p v-if="!filteredOptions.length" class="hs-custom-select__empty" role="status">{{ emptyMessage }}</p>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
export default {
  name: "HSCustomSelect",
  props: {
    modelValue: {
      type: [String, Number],
      default: "",
    },
    options: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: "Select option",
    },
    icon: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: "Search options..." },
    emptyMessage: { type: String, default: "No results found" },
    menuZIndex: {
      type: [String, Number],
      default: 4000,
    },
  },
  emits: ["update:modelValue", "change"],
  data() {
    return {
      isOpen: false,
      searchQuery: "",
      menuStyle: {},
    };
  },
  computed: {
    filteredOptions() {
      const query = this.searchQuery.trim().toLocaleLowerCase();
      if (!this.searchable || !query) return this.normalizedOptions;
      return this.normalizedOptions.filter((option) =>
        `${option.label} ${option.value}`.toLocaleLowerCase().includes(query)
      );
    },
    selectedOption() {
      return this.normalizedOptions.find((option) => this.isSelected(option.value));
    },
    normalizedOptions() {
      return this.options
        .filter((option) => option !== null && option !== undefined)
        .map((option) => {
          if (typeof option === "object") {
            return {
              value: option.value ?? "",
              label: option.label ?? option.name ?? String(option.value ?? ""),
            };
          }
          return {
            value: option,
            label: String(option),
          };
        });
    },
  },
  mounted() {
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleDocumentKeydown);
    window.addEventListener("resize", this.updateMenuPosition);
    window.addEventListener("scroll", this.updateMenuPosition, true);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleDocumentKeydown);
    window.removeEventListener("resize", this.updateMenuPosition);
    window.removeEventListener("scroll", this.updateMenuPosition, true);
  },
  methods: {
    toggleOpen() {
      if (this.disabled) return;
      if (this.isOpen) this.isOpen = false;
      else this.openMenu();
    },
    openMenu() {
      if (this.disabled) return;
      this.searchQuery = "";
      this.isOpen = true;
      this.$nextTick(() => {
        this.updateMenuPosition();
        if (this.searchable) this.$refs.search?.focus();
      });
    },
    selectOption(option) {
      this.$emit("update:modelValue", option.value);
      this.$emit("change", option.value);
      this.isOpen = false;
      this.$refs.trigger?.focus();
    },
    handleMenuKeydown(event) {
      if (event.key === "Tab") {
        this.isOpen = false;
        this.$refs.trigger?.focus();
        return;
      }
      const options = Array.from(this.$refs.menu?.querySelectorAll('[role="option"]') || []);
      const index = options.indexOf(document.activeElement);
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const next = event.key === "ArrowDown" ? Math.min(index + 1, options.length - 1) : index - 1;
        if (next < 0) this.$refs.search?.focus();
        else options[next]?.focus();
      } else if (event.key === "Enter" && event.target === this.$refs.search) {
        event.preventDefault();
        if (this.filteredOptions.length) this.selectOption(this.filteredOptions[0]);
      }
    },
    isSelected(value) {
      return String(value) === String(this.modelValue);
    },
    handleDocumentClick(event) {
      if (
        !this.$refs.root?.contains(event.target) &&
        !this.$refs.menu?.contains(event.target)
      ) {
        this.isOpen = false;
      }
    },
    handleDocumentKeydown(event) {
      if (event.key === "Escape" && this.isOpen) {
        this.isOpen = false;
        this.$refs.trigger?.focus();
      }
    },
    updateMenuPosition() {
      if (!this.isOpen || !this.$refs.root) return;

      const rect = this.$refs.root.getBoundingClientRect();
      const viewportPadding = 12;
      const availableRight = window.innerWidth - viewportPadding;
      const preferredWidth = Math.min(Math.max(rect.width, this.searchable ? 320 : 220), window.innerWidth - viewportPadding * 2);
      const left = Math.min(rect.left, availableRight - preferredWidth);
      const gap = 8;
      const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
      const spaceAbove = rect.top - viewportPadding;
      const openAbove = spaceBelow < 220 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(
        160,
        Math.min(280, openAbove ? spaceAbove - gap : spaceBelow - gap),
      );
      const top = openAbove
        ? Math.max(viewportPadding, rect.top - gap - maxHeight)
        : rect.bottom + gap;

      this.menuStyle = {
        left: `${Math.max(viewportPadding, left)}px`,
        top: `${top}px`,
        width: `${preferredWidth}px`,
        zIndex: String(this.menuZIndex),
        maxHeight: `${maxHeight}px`,
      };
    },
  },
};
</script>

<style scoped>
.hs-custom-select {
  min-width: 190px;
  position: relative;
}

.hs-custom-select__button {
  align-items: center;
  background: var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 16px;
  color: var(--hs-muted, #64748b);
  display: inline-flex;
  gap: 9px;
  min-height: 42px;
  padding: 0 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  width: 100%;
}

.hs-custom-select__button:hover,
.is-open .hs-custom-select__button {
  border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 40%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent);
}

.hs-custom-select__label {
  color: var(--hs-text, #0f172a);
  flex: 1;
  font-size: 0.86rem;
  font-weight: 800;
  min-width: 0;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hs-custom-select__label.is-placeholder {
  color: var(--hs-muted, #64748b);
}

.hs-custom-select__chevron {
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.is-open .hs-custom-select__chevron {
  transform: rotate(180deg);
}

.hs-custom-select__menu {
  background:
    radial-gradient(circle at 90% 0%, color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent), transparent 32%),
    var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 18px;
  box-shadow: 0 22px 54px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-height: 280px;
  overflow: hidden;
  padding: 8px;
  position: fixed;
  z-index: 4000;
}

.hs-custom-select__options {
  overflow-y: auto;
  min-height: 0;
}

.hs-custom-select__search {
  flex: 0 0 auto;
  padding: 4px 4px 8px;
}

.hs-custom-select__search-input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid var(--hs-border, #dbe4f0);
  border-radius: 10px;
  background: var(--hs-surface, #fff);
  color: var(--hs-text, #0f172a);
  padding: 10px 12px;
  font-size: 0.9rem;
}

.hs-custom-select__search-input:focus-visible,
.hs-custom-select__option:focus-visible {
  outline: 2px solid var(--hs-primary, #3293ec);
  outline-offset: -2px;
}

.hs-custom-select__empty {
  padding: 16px 10px;
  color: var(--hs-muted, #64748b);
  font-size: 0.86rem;
}

.hs-custom-select__option {
  align-items: center;
  border-radius: 13px;
  color: var(--hs-text, #0f172a);
  display: flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 12px;
  justify-content: space-between;
  min-height: 38px;
  padding: 0 10px;
  text-align: left;
  transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease;
  width: 100%;
}

.hs-custom-select__option:hover {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent);
  color: var(--hs-primary, #3293ec);
  transform: translateX(2px);
}

.hs-custom-select__option.is-selected {
  background: linear-gradient(135deg, color-mix(in srgb, var(--hs-primary, #3293ec) 16%, transparent), transparent);
  color: var(--hs-primary, #3293ec);
}

.is-disabled {
  opacity: 0.62;
  pointer-events: none;
}

.hs-custom-select-pop-enter-active,
.hs-custom-select-pop-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.hs-custom-select-pop-enter-from,
.hs-custom-select-pop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (max-width: 720px) {
  .hs-custom-select {
    width: 100%;
  }
}
</style>
